import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import { LayoutGuest } from "../../boxes/LayoutGuest/LayoutGuest";
import { authService } from "../../services/AuthService";
import classes from "./Login.module.scss";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { Input } from "../../shared/Input/Input";
import { useFormTwoFactorLogin } from "./hooks/useFormTwoFactorLogin";
import { TWO_FACTOR_TYPES } from "../../consts/api";
import { InputError } from "../../shared/InputError/InputError";
import { Button } from "../../shared/Button/Button";
import { CircularProgress } from "../../shared/CircularProgress/CircularProgress";
import { useTwoFactorLogin } from "./hooks/useTwoFactorLogin";
import { Checkbox } from "../../shared/Checkbox/Checkbox";
import { AlternativeOtp } from "./shared/AlternativeOtp";
import { useResendOtp } from "./hooks/useResendOtp";
import { LOCAL_STORAGE_KEYS } from "../../consts/localStorage";
import { AlertBox } from "../../shared/AlertBox/AlertBox";
import { useFnCallRateLimit } from "../../widgets/hooks/useFnCallRateLimit";
import { useFnCallInterval } from "../../widgets/hooks/useFnCallInterval";
import { useOtpFailover } from "./hooks/useOtpFailover";
import { OtpFailoverPanel } from "./shared/OtpFailoverPanel";
import { useResend2FaOtpMutation } from "../../api/mutations/useResend2FaOtpMutation";
import { uiNotification } from "../../services/UINotificationService";
import { extractApiError } from "../../utilities/api";

function TwoFactorLogin({ history }) {
  const { twoFactorType, otpSentTo, trustDays } = authService.getPreLoginData();

  const { tCommon } = useAppTranslation.Common();
  const { tAuth } = useAppTranslation.Auth();
  const { verify, isVerifying } = useTwoFactorLogin();
  const [isAlternativeOpen, setIsAlternativeOpen] = useState(false);
  const [otpDelivery, setOtpDelivery] = useState(() =>
    twoFactorType === TWO_FACTOR_TYPES.sms
      ? authService.getPreLoginData().otpDelivery || null
      : null,
  );

  const { mutateAsync: sendOtpFailover } = useResend2FaOtpMutation();
  const otpFailover = useOtpFailover({
    delivery: otpDelivery,
    onFailover: async () => {
      const preLoginData = authService.getPreLoginData();
      try {
        const res = await sendOtpFailover({
          method: TWO_FACTOR_TYPES.sms,
          "2fa_resend_token": preLoginData.resendToken2Fa,
          wpuId: preLoginData.wpuId,
          otp_failover: true,
        });
        return res.data.data.otp;
      } catch (e) {
        throw new Error(extractApiError(e));
      }
    },
  });

  const resendRateLimit = useFnCallRateLimit({
    id: LOCAL_STORAGE_KEYS.twoFactorRateLimit,
    maxAttempts: 3,
    initialAttemptsAmount: 0,
    cooldownId: LOCAL_STORAGE_KEYS.twoFactorCooldownTimestamp,
    cooldownMs: 15 * 60 * 1000,
    message: tAuth("twoFactor.alternativeOtp.info.resendLimit"),
  });

  const resendCooldown = useFnCallInterval({
    id: LOCAL_STORAGE_KEYS.twoFactorResendUntil,
    cdSeconds: 30,
  });

  const {
    form,
    isFormValid,
    onFormValueBlur,
    onFormValueChange,
    submit,
    hasError,
    errors,
    setFieldValue,
  } = useFormTwoFactorLogin({
    onSubmit: (values, handlers) =>
      verify(values, twoFactorType, handlers).then(() => {
        const verifiedVia = otpFailover.markVerified();
        if (verifiedVia.usedFailover) {
          uiNotification.success(
            tAuth("twoFactor.otpFailover.verified.afterFailover"),
          );
        }
      }),
  });

  const { initiate: resendOtp, isLoading: isLoadingResend } = useResendOtp();

  const withOtpResend =
    twoFactorType === TWO_FACTOR_TYPES.email ||
    twoFactorType === TWO_FACTOR_TYPES.sms;

  const securityQuestion = authService.getPreLoginData().securityQuestion;
  const isPasscodeSet = authService.getPreLoginData().isPasscodeSet;

  const codePlaceholderByType = {
    [TWO_FACTOR_TYPES.email]: tAuth("label.enterOtp"),
    [TWO_FACTOR_TYPES.sms]: tAuth("label.enterOtp"),
    [TWO_FACTOR_TYPES.google]: tAuth("label.enterToken"),
  };

  useEffect(() => {
    if (!twoFactorType) {
      history.goBack();
    }
  }, [history]);

  const handleResend = () => {
    resendRateLimit.call(
      () =>
        resendCooldown.callAsync(async () => {
          const res = await resendOtp();
          // A new SMS code restarts the voice call countdown
          if (twoFactorType === TWO_FACTOR_TYPES.sms) {
            setOtpDelivery(res?.data?.data?.otp || null);
          }
        }),
      () => {
        setIsAlternativeOpen(true);
      },
    );
  };

  const isAlternativeWayAvailable = Boolean(securityQuestion || isPasscodeSet);

  return (
    <LayoutGuest>
      <div className={classes.root}>
        <h1 className={classes.header}>{tAuth("twoFactor.header")}</h1>
        <AlertBox size="small" variant="success" className={classes.alert}>
          {tAuth("twoFactor.enterCode", { to: otpSentTo })}
        </AlertBox>
        <OtpFailoverPanel
          state={otpFailover.state}
          secondsLeft={otpFailover.secondsLeft}
        />
        <div className={classes.form}>
          <div>
            <Input
              autoFocus
              type="code"
              name="code"
              value={form.code}
              onChange={onFormValueChange}
              onBlur={onFormValueBlur}
              isError={hasError("code")}
              isDisabled={isVerifying}
              placeholder={codePlaceholderByType[twoFactorType]}
            />
            {hasError("code") && <InputError>{errors.code}</InputError>}
          </div>
          {!!withOtpResend && (
            <Button
              onClick={handleResend}
              variant="text"
              fullWidth
              isDisabled={isLoadingResend || resendCooldown.cd > 0}
            >
              {resendCooldown.cd > 0
                ? `${tAuth("twoFactor.alternativeOtp.resendIn")} ${
                    resendCooldown.cd
                  }s`
                : tAuth("twoFactor.alternativeOtp.resendOtp")}
            </Button>
          )}
          <Button
            fullWidth
            onClick={submit}
            isDisabled={!isFormValid || isVerifying}
            leftAdornment={
              isVerifying ? (
                <CircularProgress size="small" color="white" />
              ) : undefined
            }
          >
            {tCommon("label.continue")}
          </Button>
          <Checkbox
            labelClassName={classes.trustDeviceLabel}
            label={tAuth("twoFactor.trustDevice", { days: trustDays || 14 })}
            isChecked={form.trustDevice}
            onChange={() => setFieldValue("trustDevice", !form.trustDevice)}
          />
          {isAlternativeWayAvailable && (
            <button
              className={classes.anotherWay}
              onClick={() => {
                // The user picked another way, so don't call them anymore
                setOtpDelivery(null);
                setIsAlternativeOpen(true);
              }}
            >
              {tAuth("twoFactor.anotherWay")}
            </button>
          )}
        </div>
      </div>
      {isAlternativeOpen && isAlternativeWayAvailable && (
        <AlternativeOtp onClose={() => setIsAlternativeOpen(false)} />
      )}
    </LayoutGuest>
  );
}

TwoFactorLogin.propTypes = {
  history: PropTypes.object,
};

export default withRouter(TwoFactorLogin);
